// Clojure course content — Part 3 (lessons 22–24): advanced topics

export const lessonsPart3 = [
  {
    id: "namespaces",
    title: "Namespaces & Libraries",
    group: "Advanced Clojure",
    summary: "Organise code and reach for Clojure's standard library.",
    content: [
      { t: "p", text: "As programs grow, you organise names into namespaces — think of them as labelled drawers that keep related functions together and avoid clashes. Clojure's own standard library is split into namespaces like clojure.string and clojure.set." },
      { t: "h", text: "require and aliases" },
      { t: "p", text: "To use another namespace, you require it, usually giving it a short alias with :as. Then you call its functions with alias/function." },
      { t: "code", code: "(require '[clojure.string :as str])\n(str/upper-case \"hello\")" },
      { t: "p", text: "The quote (that ' mark) tells Clojure the [clojure.string :as str] part is data describing what to load, not code to run." },
      { t: "h", text: "clojure.string" },
      { t: "p", text: "This namespace has handy text tools: join, split, trim, replace, includes? and more." },
      { t: "code", code: "(require '[clojure.string :as str])\n(str/join \"-\" [\"2025\" \"06\" \"15\"])" },
      { t: "code", code: "(require '[clojure.string :as str])\n(str/replace \"hello world\" \"o\" \"0\")" },
      { t: "p", text: "Two you'll use constantly are str/upper-case and str/lower-case, which change the case of every letter. They're a common way to normalise text so comparisons ignore capitalisation." },
      { t: "code", code: "(require '[clojure.string :as str])\n(str/lower-case \"Hello WORLD\")" },
      { t: "h", text: "clojure.set" },
      { t: "p", text: "For working with sets, clojure.set provides union, intersection and difference." },
      { t: "code", code: "(require '[clojure.set :as set])\n(set/intersection #{1 2 3} #{2 3 4})" },
      { t: "note", text: "In this course clojure.string and clojure.set are always available as str and set, so you can call them even without writing require first." },
    ],
    exercises: [
      {
        prompt: "Using clojure.string, write `shout` that trims a string and upper-cases it. e.g. (shout \"  hi \") => \"HI\".",
        starter: "(require '[clojure.string :as str])\n\n(defn shout [s]\n  )",
        tests: ["(= (shout \"  hi \") \"HI\")", "(= (shout \"quiet\") \"QUIET\")"],
        solution: "(require '[clojure.string :as str])\n\n(defn shout [s]\n  (str/upper-case (str/trim s)))",
        hint: "Combine str/trim and str/upper-case.",
      },
      {
        prompt: "Using clojure.set, write `common` that returns the values present in BOTH sets.",
        starter: "(require '[clojure.set :as set])\n\n(defn common [a b]\n  )",
        tests: ["(= (common #{1 2 3} #{2 3 4}) #{2 3})", "(= (common #{:a} #{:b}) #{})"],
        solution: "(require '[clojure.set :as set])\n\n(defn common [a b]\n  (set/intersection a b))",
        hint: "set/intersection returns the shared elements.",
      },
    ],
  },

  {
    id: "interop",
    title: "Talking to JavaScript",
    group: "Advanced Clojure",
    summary: "Because your Clojure runs as JavaScript, you can call JS directly.",
    content: [
      { t: "p", text: "Remember that here Clojure runs as JavaScript in your browser. That means you can reach out to JavaScript's own functions and objects when you need them. This is called interop." },
      { t: "h", text: "Calling methods" },
      { t: "p", text: "To call a method on an object, put a dot before the method name: (.methodName object args). For example, JavaScript strings have a toUpperCase method." },
      { t: "code", code: "(.toUpperCase \"hello\")" },
      { t: "h", text: "Reading properties" },
      { t: "p", text: "To read a property (not call it), use a dot-dash: (.-propertyName object). A JavaScript string has a length property." },
      { t: "code", code: "(.-length \"clojure\")" },
      { t: "h", text: "Global JS objects" },
      { t: "p", text: "Reach global JavaScript things through the js/ prefix, like js/Math." },
      { t: "code", code: "(js/Math.max 3 9 2)" },
      { t: "code", code: "(js/Math.round 4.6)" },
      { t: "note", text: "This runtime also happens to accept JVM-style Math/ calls — (Math/sqrt 16) returns 4 — because it maps the Math/ prefix onto JavaScript's Math object. But that only covers method-style calls: parentheses always mean 'call it', so reading a constant fails — (Math/PI) errors, and even (js/Math.PI) with parentheses does too. To read a constant, use the bare form with no parentheses: js/Math.PI gives 3.14159…. In short, stick with js/ for everything: (js/Math.sqrt 16) calls a function, js/Math.PI reads a value." },
      { t: "note", text: "Most of the time you'll stay in pure Clojure — it's cleaner. Interop is your escape hatch for when you specifically need a JavaScript capability." },
    ],
    exercises: [
      {
        prompt: "Write `loud` that returns the upper-cased version of a string using JavaScript interop (the .toUpperCase method).",
        starter: "(defn loud [s]\n  )",
        tests: ["(= (loud \"hey\") \"HEY\")", "(= (loud \"abc\") \"ABC\")"],
        solution: "(defn loud [s]\n  (.toUpperCase s))",
        hint: "(.toUpperCase s).",
      },
      {
        prompt: "Write `biggest` that returns the largest of three numbers using js/Math.max.",
        starter: "(defn biggest [a b c]\n  )",
        tests: ["(= (biggest 3 9 2) 9)", "(= (biggest 10 1 5) 10)", "(= (biggest -1 -5 -2) -1)"],
        solution: "(defn biggest [a b c]\n  (js/Math.max a b c))",
        hint: "(js/Math.max a b c).",
      },
    ],
  },

  {
    id: "macros",
    title: "A Taste of Macros",
    group: "Advanced Clojure",
    summary: "Code that writes code — Clojure's superpower.",
    content: [
      { t: "p", text: "Here's something most languages can't do: in Clojure, code is just data (lists, symbols, vectors). A macro is a function that runs BEFORE your program does, taking code as input and returning new code to run in its place. This lets you invent your own syntax." },
      { t: "p", text: "You define one with defmacro. Instead of receiving evaluated values like a normal function, a macro receives the raw, unevaluated code." },
      { t: "code", code: "(defmacro my-unless [test body]\n  (list 'if test nil body))\n\n;; run this snippet, then run the next one" },
      { t: "p", text: "That macro builds a new expression: (if test nil body). We use (list 'if ...) to construct that code as data. The quote before if means the symbol if itself, not its value. After running the definition above, try using it:" },
      { t: "code", code: "(my-unless false \"this runs because the test is false\")" },
      { t: "p", text: "When Clojure sees (my-unless false \"...\"), it first expands it into (if false nil \"...\") and then runs THAT. You can watch the expansion with macroexpand:" },
      { t: "code", code: "(defmacro my-unless [test body]\n  (list 'if test nil body))\n\n(macroexpand '(my-unless false \"hi\"))" },
      { t: "note", text: "Macros are an advanced tool — you'll use them rarely, and most of Clojure's own control-flow forms (like when and cond) are actually macros built this way. Understanding that they exist is a great milestone." },
    ],
    exercises: [
      {
        prompt: "Write a macro `twice` that takes an expression and returns code that runs it two times using do. e.g. (twice (foo)) should expand to (do (foo) (foo)).",
        starter: "(defmacro twice [expr]\n  )",
        tests: ["(= (macroexpand '(twice (println 1))) '(do (println 1) (println 1)))", "(let [a (atom 0)] (twice (swap! a inc)) (= @a 2))"],
        solution: "(defmacro twice [expr]\n  (list 'do expr expr))",
        hint: "Return (list 'do expr expr) so the expression appears twice inside a do.",
      },
      {
        prompt: "Write a macro `unless` (the opposite of when): (unless test body) should run body only when test is falsey. Expand to (if test nil body).",
        starter: "(defmacro unless [test body]\n  )",
        tests: ["(= (unless false 99) 99)", "(= (unless true 99) nil)", "(= (macroexpand '(unless x y)) '(if x nil y))"],
        solution: "(defmacro unless [test body]\n  (list 'if test nil body))",
        hint: "Build the list (if test nil body) with (list 'if test nil body).",
      },
    ],
  },
];
